import { useNavigate } from "react-router-dom";
import { Bell, Swords, MapPin, Trophy, ClipboardCheck, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotifications } from "@/context/NotificationsContext";

const ICONS = { challenge: Swords, challenge_accepted: Swords, challenge_declined: Swords, court_status: MapPin, ladder_challenge: Trophy, match_confirm: ClipboardCheck };

function ago(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const { items, unread, connected, markAllRead, markRead } = useNotifications();
  const navigate = useNavigate();

  const open = (n) => { markRead(n.notification_id); navigate(n.link || "/play"); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-btn" className="relative w-9 h-9 rounded-full hover:bg-white/10 grid place-items-center">
          <Bell size={18} />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full pv-bg-primary text-black text-[10px] font-extrabold grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 sm:w-96 p-0 rounded-2xl overflow-hidden" data-testid="notifications-panel">
        <div className="pv-bg-dark text-white px-4 py-3 flex items-center justify-between">
          <div className="font-display font-bold flex items-center gap-2">
            Alerts
            <span data-testid="ws-status" title={connected ? "Live" : "Reconnecting"} className={`w-2 h-2 rounded-full ${connected ? "bg-[#BED445]" : "bg-red-400"}`} />
          </div>
          {unread > 0 && (
            <button data-testid="mark-all-read-btn" onClick={markAllRead} className="text-xs font-bold text-white/80 hover:text-white flex items-center gap-1">
              <CheckCheck size={14} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto divide-y">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="notifications-empty">Quiet court. No alerts yet.</div>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.notification_id} data-testid={`notification-${n.notification_id}`} onClick={() => open(n)}
                className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-[#F8F6F3] transition-colors ${n.read ? "" : "bg-[#BED445]/10"}`}>
                <div className="w-9 h-9 rounded-full grid place-items-center shrink-0 pv-bg-dark text-white"><Icon size={16} /></div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm pv-text-dark ${n.read ? "font-semibold" : "font-extrabold"}`}>{n.title}</div>
                  <div className="text-xs text-gray-600 line-clamp-2">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">{ago(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full pv-bg-primary mt-2 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
